import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
    KeepAliveIframeController,
    KeepAliveIframeProtocol
} from "../../utils/keep-alive-iframe-controller";

// Keeps one embedded app alive across host UI changes: the iframe element is
// created once, hidden with CSS (never display:none, never unmounted or moved)
// and talks the keep-alive postMessage contract through the controller. The
// parent decides when to create/destroy the component itself (lazy mount,
// idle release via @released, tab close).
@Component({
    template: require("./template.html")
})
export default class KeepAliveIframe extends Vue {
    @Prop({ required: true }) src: string;
    @Prop({ required: true }) origin: string;
    @Prop({ required: true }) protocol: KeepAliveIframeProtocol;
    @Prop({ required: true }) idleReleaseMs: number;
    @Prop({ required: true }) dirtyQueryTimeoutMs: number;
    @Prop({ default: false }) hidden: boolean;
    @Prop({ default: null }) frameId: string;
    @Prop({ default: "" }) allow: string;

    isLoading = true;

    private controller: KeepAliveIframeController = null;

    get iframe(): HTMLIFrameElement {
        return this.$refs.frame as HTMLIFrameElement;
    }

    mounted() {
        this.controller = new KeepAliveIframeController({
            origin: this.origin,
            protocol: this.protocol,
            idleReleaseMs: this.idleReleaseMs,
            dirtyQueryTimeoutMs: this.dirtyQueryTimeoutMs,
            getContentWindow: () => this.iframe?.contentWindow || null,
            onRelease: () => this.$emit("released")
        });
        window.addEventListener("message", this.onWindowMessage);
        this.controller.setHidden(this.hidden);
        // Assigned once; a changed `src` prop is deliberately not applied (see template).
        this.iframe.src = this.src;
    }

    beforeDestroy() {
        window.removeEventListener("message", this.onWindowMessage);
        this.controller.dispose();
        this.controller = null;
    }

    @Watch("hidden")
    onHiddenChanged(hidden: boolean) {
        this.controller?.setHidden(hidden);
    }

    @Watch("src")
    onSrcChanged() {
        console.warn("KeepAliveIframe: src changed after mount is ignored; destroy and re-create the component instead.", { frameId: this.frameId });
    }

    onLoad() {
        this.isLoading = false;
        this.$emit("load");
    }

    private onWindowMessage(event: MessageEvent) {
        this.controller?.handleMessage(event);
    }
}
